/**
 * AWS Amplify Configuration for Cognito Authentication
 * 
 * This configuration sets up AWS Amplify to work with Amazon Cognito
 * for user authentication. It supports OAuth flow with localhost callbacks
 * for local development.
 * 
 * Environment Variables Required:
 * - VITE_AWS_REGION: AWS region where Cognito is deployed
 * - VITE_COGNITO_USER_POOL_ID: Cognito User Pool ID
 * - VITE_COGNITO_CLIENT_ID: Cognito App Client ID
 * - VITE_COGNITO_DOMAIN: Cognito domain for OAuth (e.g., your-domain.auth.us-east-1.amazoncognito.com)
 * - VITE_REDIRECT_URL: OAuth callback URL (e.g., http://localhost:3000 for local dev)
 */

import { ResourcesConfig } from 'aws-amplify';

/**
 * Validates that all required environment variables are present
 * @throws Error if any required environment variable is missing
 */
function validateEnvironmentVariables(): void {
    const required = [
        'VITE_AWS_REGION',
        'VITE_COGNITO_USER_POOL_ID',
        'VITE_COGNITO_CLIENT_ID',
        'VITE_COGNITO_DOMAIN',
        'VITE_REDIRECT_URL',
    ];

    const missing = required.filter((key) => !import.meta.env[key]);

    if (missing.length > 0) {
        throw new Error(
            `Missing required environment variables: ${missing.join(', ')}\n` +
            'Please check your .env file and ensure all Cognito configuration variables are set.'
        );
    }
}

// Validate environment variables on module load
validateEnvironmentVariables();

/**
 * AWS Amplify configuration for Cognito authentication
 * 
 * This configuration enables:
 * - OAuth 2.0 authorization code flow
 * - Localhost callback support for local development
 * - OpenID Connect scopes (openid, email, profile)
 * - Automatic token refresh
 */
export const amplifyConfig: ResourcesConfig = {
    Auth: {
        Cognito: {
            // AWS region where Cognito User Pool is deployed
            userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID,

            // Cognito App Client ID (must have OAuth enabled)
            userPoolClientId: import.meta.env.VITE_COGNITO_CLIENT_ID,

            // OAuth configuration for hosted UI
            loginWith: {
                oauth: {
                    // Cognito domain for OAuth endpoints
                    domain: import.meta.env.VITE_COGNITO_DOMAIN,

                    // OAuth scopes to request
                    // - openid: Required for OIDC
                    // - email: Access to user's email
                    // - profile: Access to user's profile information
                    scopes: ['openid', 'email', 'profile'],

                    // Redirect URLs after successful authentication
                    // For local development: http://localhost:3000
                    // For production: https://your-domain.com
                    redirectSignIn: [import.meta.env.VITE_REDIRECT_URL],

                    // Redirect URLs after sign out
                    redirectSignOut: [import.meta.env.VITE_REDIRECT_URL],

                    // OAuth 2.0 response type
                    // 'code' = Authorization Code flow (recommended for web apps)
                    responseType: 'code',
                },
            },
        },
    },
};

/**
 * Helper function to get the current redirect URL
 * Useful for debugging and logging
 */
export function getRedirectUrl(): string {
    return import.meta.env.VITE_REDIRECT_URL;
}

/**
 * Helper function to get the Cognito domain
 * Useful for debugging and logging
 */
export function getCognitoDomain(): string {
    return import.meta.env.VITE_COGNITO_DOMAIN;
}

/**
 * Helper function to check if running in local development mode
 */
export function isLocalDevelopment(): boolean {
    return import.meta.env.VITE_ENVIRONMENT === 'local' ||
        import.meta.env.VITE_REDIRECT_URL.includes('localhost');
}
